import { Link } from 'react-router-dom';
import {
  AtSign,
  Bell,
  Bot,
  Boxes,
  Building2,
  CalendarClock,
  Check,
  ClipboardList,
  Filter,
  KeyRound,
  Layers,
  Link2,
  ListChecks,
  Lock,
  MessagesSquare,
  Paperclip,
  PieChart,
  Receipt,
  Search,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  StickyNote,
  Table2,
  Timer,
  TriangleAlert,
  Users,
  Wallet,
  Workflow,
} from 'lucide-react';
import { usePageMeta } from '../usePageMeta';
import { SectionHeading } from '../components/SectionHeading';
import { ImagePlaceholder } from '../components/ImagePlaceholder';
import { CtaBanner } from '../components/CtaBanner';

/** Flo's tool belt — what the agent can actually execute, not just discuss. */
const FLO_TOOLS: ReadonlyArray<readonly [tool: string, does: string]> = [
  ['manage_project', 'Create, update and find projects'],
  ['manage_task', 'Create, update, assign and move tasks'],
  ['smart_assign_tasks', 'Workload-aware task assignment'],
  ['manage_roadblock', 'Raise and resolve blockers'],
  ['manage_request', 'Create and handle project requests'],
  ['manage_note', 'Create and edit project notes'],
  ['manage_finance', 'Record income and expense entries'],
  ['manage_report', 'Generate reports on demand'],
  ['manage_application_access', 'Grant and revoke tool access'],
  ['search_projects / search_members', 'Look up anything across the workspace'],
  ['task_summary', 'Summarize current task state'],
  ['query_activity_log', 'Audit trail — what happened, and when'],
];

const TASK_TONES = ['g-violet', 'g-blue', 'g-pink', 'g-sun'] as const;

const TASK_FEATURES = [
  {
    icon: Table2,
    title: 'Table and board, same tasks',
    body: 'A dense sortable table when you are working, a drag-and-drop Kanban board when you are planning. One dataset, two ways to look at it.',
  },
  {
    icon: ListChecks,
    title: 'Subtasks that roll up',
    body: 'Expandable subtask rows with inline editing, and a progress bar that reflects them — parent completion is calculated, never guessed.',
  },
  {
    icon: Layers,
    title: 'Inline and bulk editing',
    body: 'Click any cell to change status, priority, assignee or due date. Multi-select to change them for fifty tasks at once.',
  },
  {
    icon: Filter,
    title: 'Filters on everything',
    body: 'Status, priority, assignee, project, approval state, billable state, due-date and charged-date ranges — plus search by TSK-123 or title.',
  },
  {
    icon: Check,
    title: 'Approvals as a stage',
    body: 'Tasks route needs approval → ready for review → approved or rejected, on a dedicated board, with one-click and bulk approval.',
  },
  {
    icon: Share2,
    title: 'Exports that leave the app',
    body: 'Any filtered view goes out as CSV or PDF, so the task list can become the status report without being retyped.',
  },
] as const;

/** Canvas annotation types, in the product's own colours. */
const ANNOTATIONS: ReadonlyArray<readonly [label: string, ink: string, bg: string]> = [
  ['Bug', '#EF4444', '#FEF2F2'],
  ['Question', '#3B82F6', '#EFF6FF'],
  ['Idea', '#F59E0B', '#FFFBEB'],
  ['Comment', '#8B5CF6', '#F5F3FF'],
];

const COLLAB_TONES = ['g-blue', 'g-violet', 'g-pink', 'g-sun'] as const;

const COLLAB = [
  {
    icon: MessagesSquare,
    title: 'Team messaging',
    body: 'Channels and one-to-one DMs delivered over WebSockets, with live emoji reactions and file attachments. No second app to check.',
  },
  {
    icon: ClipboardList,
    title: 'Requests',
    body: 'Typed requests — general, task, file, update — with rich text, attachments, comments and status tracking, plus an org-wide Requests Hub.',
  },
  {
    icon: TriangleAlert,
    title: 'Roadblocks',
    body: 'Log a blocker, comment on it, resolve it, reopen it if it comes back. Filterable per project and across the whole organization.',
  },
  {
    icon: StickyNote,
    title: 'Notes and files',
    body: 'Rich-text project notes, and files on projects, tasks, requests, roadblocks and notes — any type, previewed, versioned by entity, on cloud storage.',
  },
  {
    icon: KeyRound,
    title: 'Application access',
    body: 'Register the tools a project depends on, grant and revoke per user, and see who has access to what from one hub. Changes notify automatically.',
  },
  {
    icon: Search,
    title: 'One search field',
    body: 'A command palette over projects, tasks, members, files, notes, requests, roadblocks and notifications. One box, the whole workspace.',
  },
] as const;

/** Role-based access control, straight from the platform's permission matrix. */
const ROLES = ['Owner', 'Manager', 'Member', 'Client'] as const;

const RBAC: ReadonlyArray<readonly [cap: string, perms: readonly [boolean, boolean, boolean, boolean]]> = [
  ['Create tasks', [true, true, true, false]],
  ['Delete tasks', [true, true, false, false]],
  ['Approve / reject tasks', [true, false, false, false]],
  ['Create projects', [true, true, true, false]],
  ['Delete projects', [true, true, false, false]],
  ['Manage organization', [true, false, false, false]],
  ['Manage members & invitations', [true, true, false, false]],
  ['View reports', [true, true, true, true]],
  ['Time tracking', [true, true, true, false]],
  ['Billing / licence', [true, false, false, false]],
  ['Mark tasks billable', [true, false, false, false]],
];

export function ProjectFlow(): React.JSX.Element {
  usePageMeta(
    'ProjectFlow — AI-assisted project & task management by Inafana',
    'ProjectFlow runs projects, tasks, approvals, time tracking, reporting, CRM and invoicing in one multi-tenant workspace — with Flo, an agentic AI project manager that executes the work and waits for your approval first.',
  );

  return (
    <>
      {/* ─────────────── hero ─────────────── */}
      <section className="product-hero">
        <div className="blob blob-violet" style={{ width: 580, height: 580, top: -170, left: '-6%' }} />
        <div className="blob blob-blue" style={{ width: 440, height: 440, bottom: -120, right: '-4%' }} />
        <div className="container">
          <div className="product-hero-grid">
            <div className="product-hero-copy">
              <img
                className="product-mark"
                src="/assets/products/projectflow.svg"
                alt="ProjectFlow"
              />
              <h1 className="h1 reveal" data-delay="1">
                Project management with{' '}
                <span className="grad">an agent inside it</span>.
              </h1>
              <p className="lede reveal" data-delay="2">
                ProjectFlow runs projects, tasks, approvals, time, invoices and clients in
                one workspace — and Flo, its built-in AI project manager, actually operates
                it. Ask for a plan and it writes the tasks, assigns them by workload, and
                waits for you to approve before it touches anything.
              </p>
              <div className="hero-ctas reveal" data-delay="3">
                <Link to="/#contact" className="btn btn-primary btn-lg">
                  Get a ProjectFlow demo <span className="arrow">→</span>
                </Link>
                <Link to="/projectflow#flo" className="btn btn-ghost btn-lg">
                  Meet Flo
                </Link>
              </div>
            </div>
            <div className="reveal" data-delay="2">
              <ImagePlaceholder
                label="ProjectFlow project workspace"
                hint="Wide shot of the project overview — stats, progress, recent activity, with the tab rail visible."
                ratio="16 / 11"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── Flo ─────────────── */}
      <section id="flo" className="section-surface">
        <div className="container">
          <SectionHeading
            anim="blur"
            title={
              <>
                An agent that <span className="si accent-text">operates the workspace</span>.
              </>
            }
            lede="Most AI in project tools summarizes what you already did. Flo does the work — creates the projects, moves the tasks, files the expense, pulls the report — and asks first."
          />

          <div className="feature-split">
            <div className="reveal">
              <ul className="feature-list">
                <li>
                  <Bot size={20} strokeWidth={1.8} />
                  <div>
                    <b>Chat, with streaming answers</b>
                    <span>
                      Natural language in, work out. Conversations are grouped, named and
                      kept, so the thread that planned a sprint is still there next month.
                    </span>
                  </div>
                </li>
                <li>
                  <ShieldCheck size={20} strokeWidth={1.8} />
                  <div>
                    <b>Human in the loop, by default</b>
                    <span>
                      Flo proposes the tool call and stops. Nothing is created, moved or
                      granted until someone approves it — the agent never acts unwatched.
                    </span>
                  </div>
                </li>
                <li>
                  <Sparkles size={20} strokeWidth={1.8} />
                  <div>
                    <b>Reachable from every screen</b>
                    <span>
                      A floating Flo button rides along the whole application, so asking is
                      never a detour to a separate AI page.
                    </span>
                  </div>
                </li>
                <li>
                  <PieChart size={20} strokeWidth={1.8} />
                  <div>
                    <b>Metered, per organization</b>
                    <span>
                      AI credits are tracked per organization, with administrator-side
                      allocation and model configuration. Spend is visible, not a surprise.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="reveal" data-delay="1">
              <ImagePlaceholder
                label="Flo chat panel mid-approval"
                hint="Show a proposed tool call with the Approve / Reject control still pending — that pause is the point."
                ratio="4 / 5"
              />
            </div>
          </div>

          <div className="pf-tools reveal" data-delay="1">
            <div className="pf-tools-head">
              <span>Tool</span>
              <span>What it does</span>
            </div>
            {FLO_TOOLS.map(([tool, does]) => (
              <div key={tool} className="pf-tools-row">
                <code>{tool}</code>
                <span>{does}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── tasks & approvals ─────────────── */}
      <section>
        <div className="container">
          <SectionHeading
            anim="wipe"
            title={
              <>
                Tasks the way you work, <span className="si accent-text">not the way they ship</span>.
              </>
            }
            lede="Five built-in statuses plus your own, four priorities, multiple assignees, custom fields per project — and an approval stage tasks have to pass through before anyone calls them done."
          />

          <div className="pf-shot reveal">
            <ImagePlaceholder
              label="Kanban board and table view, side by side"
              hint="Left: the drag-and-drop board with status columns. Right: the same tasks in the dense table with inline editing open on a cell."
              ratio="16 / 8"
            />
          </div>

          <div className="capability-grid pf-gap">
            {TASK_FEATURES.map((f, i) => (
              <article key={f.title} className="module-card reveal" data-delay={(i % 3) + 1}>
                <div className={`icon ${TASK_TONES[i % TASK_TONES.length]}`} aria-hidden="true">
                  <f.icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── time & reporting ─────────────── */}
      <section className="section-surface">
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <ImagePlaceholder
                label="Time tracking tree and a generated report"
                hint="The User → Project → Task hierarchy with totals at every level, and a donut chart from the report builder."
                ratio="4 / 3"
              />
            </div>
            <div className="reveal" data-delay="1">
              <h2 className="h2">
                Hours in, <span className="si accent-text">answers out</span>.
              </h2>
              <p className="lede">
                A timer on the task, a tree of every hour the organization logged, and a
                report builder that turns both into something a client can read — without a
                spreadsheet in between.
              </p>
              <ul className="feature-list">
                <li>
                  <Timer size={20} strokeWidth={1.8} />
                  <div>
                    <b>Live timers and manual entry</b>
                    <span>
                      Start and stop against a task with a running display, or enter start
                      and end times and let the duration calculate itself. See who on the
                      team is tracking right now.
                    </span>
                  </div>
                </li>
                <li>
                  <Boxes size={20} strokeWidth={1.8} />
                  <div>
                    <b>User → Project → Task, with totals</b>
                    <span>
                      One organization-wide view, hierarchical, totalled at every level,
                      with quick ranges for this week, this month and last — or any range
                      you pick.
                    </span>
                  </div>
                </li>
                <li>
                  <PieChart size={20} strokeWidth={1.8} />
                  <div>
                    <b>Five report views</b>
                    <span>
                      Summary, by project, by user, by task, and open tasks — with donut
                      and pie charts, an expandable task hierarchy, and PDF download.
                    </span>
                  </div>
                </li>
                <li>
                  <Link2 size={20} strokeWidth={1.8} />
                  <div>
                    <b>Shareable, password-protected links</b>
                    <span>
                      Send a client the report itself rather than an attachment. Optional
                      password, no account needed at their end. Past reports are saved and
                      reloadable.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── shared canvas ─────────────── */}
      <section>
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <h2 className="h2">
                Let the client point at <span className="si accent-text">the actual thing</span>.
              </h2>
              <p className="lede">
                The Shared Canvas draws Project → Task → Subtask as a connected map you can
                pan, zoom and rearrange — re-parent a task by dragging it. Then hand a
                stakeholder a tokenized link, and they annotate the map directly instead of
                describing it in an email.
              </p>
              <ul className="feature-list">
                <li>
                  <Workflow size={20} strokeWidth={1.8} />
                  <div>
                    <b>The plan as a graph</b>
                    <span>
                      Tasks, posts and roadblocks laid out as nodes and edges — structure
                      you can see, and change by dragging.
                    </span>
                  </div>
                </li>
                <li>
                  <Users size={20} strokeWidth={1.8} />
                  <div>
                    <b>Comment without an account</b>
                    <span>
                      Public canvas links use access tokens you can revoke. Reviewers get
                      read-and-comment; they never get a seat.
                    </span>
                  </div>
                </li>
              </ul>
              <div className="pf-anno">
                {ANNOTATIONS.map(([label, ink, bg]) => (
                  <span
                    key={label}
                    className="pf-anno-chip"
                    style={{ color: ink, background: bg, borderColor: ink }}
                  >
                    <i aria-hidden="true" style={{ background: ink }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <div className="reveal" data-delay="1">
              <ImagePlaceholder
                label="Shared canvas with client annotations"
                hint="The project graph with a few Bug / Question / Idea / Comment nodes dropped on it by a reviewer."
                ratio="4 / 3"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── CRM & finance ─────────────── */}
      <section className="section-surface">
        <div className="container">
          <SectionHeading
            anim="zoom"
            title={
              <>
                The client relationship <span className="si accent-text">and the invoice</span>.
              </>
            }
            lede="Client work does not stop at the task board. ProjectFlow carries the lead that became the project and the invoice that closes it — in the same system, against the same client organization."
          />
          <div className="pf-duo">
            <article className="pf-duo-card reveal" data-delay="1">
              <div className="pf-duo-top">
                <div className="icon g-blue" aria-hidden="true">
                  <Building2 size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">CRM</h3>
              </div>
              <ImagePlaceholder
                label="Deal pipeline with configurable stages"
                hint="The board view of deals, with a lead detail quick-view modal open over it."
                ratio="16 / 10"
              />
              <ul>
                <li>Leads with custom statuses, detail pages and a quick-view modal</li>
                <li>Customers with full records, and one-action lead → deal conversion</li>
                <li>Deal pipelines with the stages your sales process actually uses</li>
                <li>CRM tasks with their own status set, separate from delivery work</li>
                <li>Comments, activities and configurable activity types — call, meeting, email</li>
                <li>Custom fields on any CRM entity, with stored values</li>
              </ul>
            </article>

            <article className="pf-duo-card reveal" data-delay="2">
              <div className="pf-duo-top">
                <div className="icon g-sun" aria-hidden="true">
                  <Wallet size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">Finance &amp; invoicing</h3>
              </div>
              <ImagePlaceholder
                label="Invoice builder with live totals"
                hint="Line items, quantities, rates, discount and tax rate, with the printable invoice preview beside it."
                ratio="16 / 10"
              />
              <ul>
                <li>A per-project ledger of income and expense entries</li>
                <li>Configurable receipt types, with the receipt file attached to the entry</li>
                <li>Invoice builder — line items, quantities, rates, discount, tax, auto totals</li>
                <li>Multi-currency, with invoice statuses through to paid</li>
                <li>Printable and PDF output from the invoice viewer</li>
                <li>Tasks carry a billable flag, so what you invoice traces back to work</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ─────────────── collaboration layer ─────────────── */}
      <section>
        <div className="container">
          <SectionHeading
            anim="slide"
            title={
              <>
                Everything a project <span className="si accent-text">generates around itself</span>.
              </>
            }
            lede="The conversation, the blocker, the file someone needs, the tool access nobody remembered to revoke. All of it lives against the project rather than in six other places."
          />

          <div className="pf-shot reveal">
            <ImagePlaceholder
              label="Command palette open over a project"
              hint="Global search mid-query, showing mixed results — a task, a member, a file, a roadblock."
              ratio="16 / 7"
            />
          </div>

          <div className="capability-grid pf-gap">
            {COLLAB.map((c, i) => (
              <article key={c.title} className="module-card reveal" data-delay={(i % 3) + 1}>
                <div className={`icon ${COLLAB_TONES[i % COLLAB_TONES.length]}`} aria-hidden="true">
                  <c.icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">{c.title}</h3>
                <p>{c.body}</p>
              </article>
            ))}
          </div>

          <ul className="feature-list pf-inline-list reveal" data-delay="1">
            <li>
              <AtSign size={20} strokeWidth={1.8} />
              <div>
                <b>@Mentions everywhere</b>
                <span>In descriptions and comments alike, on tasks, requests and roadblocks.</span>
              </div>
            </li>
            <li>
              <Paperclip size={20} strokeWidth={1.8} />
              <div>
                <b>One rich text editor</b>
                <span>Headings, lists, links, images, tables and a character count — the same one in every field.</span>
              </div>
            </li>
            <li>
              <Bell size={20} strokeWidth={1.8} />
              <div>
                <b>Real-time notifications</b>
                <span>Organization and project level, filterable by type, across tasks, files, members, notes and access.</span>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* ─────────────── roles, security, administration ─────────────── */}
      <section className="section-surface">
        <div className="container">
          <SectionHeading
            anim="drop"
            title={
              <>
                Four roles, <span className="si accent-text">one of them is your client</span>.
              </>
            }
            lede="ProjectFlow is multi-tenant to the core — one account, many organizations, switched freely — with a Client Member role that sees the external project and nothing else."
          />

          <div className="pf-matrix-wrap reveal">
            <table className="pf-matrix">
              <caption className="sr-only">
                Capabilities available to each ProjectFlow role
              </caption>
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  {ROLES.map((r) => (
                    <th key={r} scope="col">
                      {r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RBAC.map(([cap, perms]) => (
                  <tr key={cap}>
                    <th scope="row">{cap}</th>
                    {perms.map((allowed, i) => (
                      <td key={ROLES[i]} className={allowed ? 'yes' : 'no'}>
                        {allowed ? (
                          <Check size={16} strokeWidth={2.6} aria-hidden="true" />
                        ) : (
                          <span aria-hidden="true">—</span>
                        )}
                        <span className="sr-only">{allowed ? 'Yes' : 'No'}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="feature-split pf-gap">
            <div className="reveal">
              <h2 className="h2">
                Locked down where it <span className="si accent-text">has to be</span>.
              </h2>
              <p className="lede">
                Getting in is the part that has to be boring. Email and password or Google,
                verified by OTP, hardened with MFA, rate limited, and logged — plus a back
                office that is a separate application with its own login.
              </p>
              <ul className="feature-list">
                <li>
                  <Lock size={20} strokeWidth={1.8} />
                  <div>
                    <b>MFA, OTP verification, rate limiting</b>
                    <span>
                      Email OTP or an authenticator app (TOTP). Accounts stay inactive
                      until a 6-digit code verifies them. Every auth event is logged.
                    </span>
                  </div>
                </li>
                <li>
                  <CalendarClock size={20} strokeWidth={1.8} />
                  <div>
                    <b>Invitation-driven joining</b>
                    <span>
                      Invites prefill the email and auto-join on registration, with expiry
                      and pending / accepted / expired status tracked per invite.
                    </span>
                  </div>
                </li>
                <li>
                  <ShieldCheck size={20} strokeWidth={1.8} />
                  <div>
                    <b>An isolated admin console</b>
                    <span>
                      Its own login, its own 4-hour session token, its own MFA and rate
                      limiting. Administrator accounts are created by server-side CLI only
                      — there is no admin signup form to attack.
                    </span>
                  </div>
                </li>
                <li>
                  <Receipt size={20} strokeWidth={1.8} />
                  <div>
                    <b>Licence and seat tracking</b>
                    <span>
                      Seats and usage are tracked against the organization's licence, with
                      subscription management, AI credit allocation and model settings
                      handled from the console.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="reveal" data-delay="1">
              <ImagePlaceholder
                label="Administration console"
                hint="The isolated back office — users, organizations, licences and AI credit allocation."
                ratio="4 / 3"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── platform ─────────────── */}
      <section>
        <div className="container">
          <SectionHeading
            anim="float"
            title={
              <>
                Three ways in, <span className="si accent-text">one workspace</span>.
              </>
            }
            lede="React 19 and Vite on the front, Node and PostgreSQL behind it, Socket.IO for anything live, S3 for anything uploaded. Light and dark, remembered per user, responsive down to a phone."
          />
          <div className="pf-platform">
            <figure className="pf-platform-item reveal" data-delay="1">
              <ImagePlaceholder
                label="Web application"
                hint="Full desktop layout, dark mode, board view."
                ratio="4 / 3"
              />
              <figcaption>
                <b>Web</b>
                <span>The full workspace — every module, every view.</span>
              </figcaption>
            </figure>
            <figure className="pf-platform-item reveal" data-delay="2">
              <ImagePlaceholder
                label="Android application"
                hint="Phone frame showing the task list and a running timer."
                ratio="4 / 3"
              />
              <figcaption>
                <b>Android</b>
                <span>Native app for the work that happens away from a desk.</span>
              </figcaption>
            </figure>
            <figure className="pf-platform-item reveal" data-delay="3">
              <ImagePlaceholder
                label="Browser extension"
                hint="The extension popup capturing a task from an open page."
                ratio="4 / 3"
              />
              <figcaption>
                <b>Extension</b>
                <span>Capture work from wherever you found it.</span>
              </figcaption>
            </figure>
          </div>

          <ul className="feature-list pf-inline-list reveal" data-delay="1">
            <li>
              <Smartphone size={20} strokeWidth={1.8} />
              <div>
                <b>Light and dark, remembered</b>
                <span>Per-user preference, and a layout that holds up from desktop to phone.</span>
              </div>
            </li>
            <li>
              <Sparkles size={20} strokeWidth={1.8} />
              <div>
                <b>Built to feel fast</b>
                <span>Skeletons, toasts, debounced search, pagination, lazy-loaded routes and keyboard shortcuts.</span>
              </div>
            </li>
            <li>
              <Table2 size={20} strokeWidth={1.8} />
              <div>
                <b>Custom fields per project</b>
                <span>Define the fields your projects need — the schema bends before your process does.</span>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <CtaBanner
        title={
          <>
            See ProjectFlow run <span className="si">your projects</span>.
          </>
        }
        lede="Tell us how your team tracks work, bills for it, and talks to clients today. We'll show you the same week inside ProjectFlow — Flo included."
        primaryLabel="Get a ProjectFlow demo"
      />
    </>
  );
}
